import { Box, Text } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { SettingButton } from 'components/common/settings-dialog/button';
import { SettingForm } from 'components/common/settings-dialog/form';
import { InactiveCallout } from 'components/common/settings-dialog/inactive-callout';
import { SettingRow } from 'components/common/settings-dialog/row';
import { MachineContext, MachineDialogMode } from 'contexts/machine.context';
import { SessionEventsContext } from 'contexts/session-events.context';
import { addSeconds, isFuture } from 'date-fns';
import { t } from 'i18next';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { DataCollectionType } from 'lib_ts/enums/data-collection.enums';
import {
  StaticVideoType,
  TroubleshootingPitchID,
  WsMsgType,
} from 'lib_ts/enums/machine-msg.enum';
import { PitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import { IBallStatusMsg } from 'lib_ts/interfaces/machine-msg/i-ball-status';
import { SpecialMsPosition } from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { IPitch, ITrainingPitch } from 'lib_ts/interfaces/pitches';
import { first } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { DataCollectionsService } from 'services/data-collections.service';
import { useAuthStore } from 'stores/use-auth-store';
import useMachineStatusStore from 'stores/use-machine-status-store';

const COMPONENT_NAME = 'MachineMaintenanceTab';

// for auto rapid fire
const AUTOFIRE_WAIT_SEC = 2;

// brief pause before starting autofire for safety
const AUTOFIRE_DELAY_SEC = 5;

// how many times to retry dropball before exiting empty carousel mode
const AUTOFIRE_DROP_RETRIES = 10;

export const MachineMaintenanceTab = () => {
  const current = useAuthStore((s) => s.current);

  const r2fDate = useMachineStatusStore((s) => s.r2fDate);
  const r2fReady = useMachineStatusStore((s) => s.r2fReady);

  const { setDialogSettings } = useContext(SessionEventsContext);

  const {
    autoFire,
    isActive,
    lastBallCount,
    lastDcComplete,
    lastMSHash,
    machine,
    specialMode,
    dropball,
    fire,
    getDefaultPitch,
    restartOS,
    sendTarget,
    setAutoFire,
    openDialog,
    setSpecialMode,
    specialMsTarget,
  } = useContext(MachineContext);

  const [pitch, setPitch] = useState<Partial<IPitch>>(getDefaultPitch());

  const [dropRetries, setDropRetries] = useState(0);

  useEffect(() => {
    if (!isActive) {
      console.debug('Skipped drop ball trigger (user is not active)');
      return;
    }

    if (dropRetries === 0) {
      console.debug('Skipped drop ball trigger (count is 0)');
      return;
    }

    dropball({
      source: `${COMPONENT_NAME}: auto-retry ${dropRetries}`,
      notifyResult: false,
    });
  }, [dropRetries, isActive]);

  const [dialogRestart, setDialogRestart] = useState<number>();

  const exitAutoFire = useCallback(() => {
    if (!autoFire) {
      return;
    }

    if (!specialMode) {
      return;
    }

    setSpecialMode(undefined);
    setAutoFire(false);

    specialMsTarget(SpecialMsPosition.lowered);

    setDropRetries(0);
  }, [autoFire, specialMode, specialMsTarget]);

  const [ignoreR2fUntil, setIgnoreR2fUntil] = useState(new Date());

  const toggleAutoFire = useCallback(async () => {
    if (!isActive) {
      return;
    }

    if (autoFire) {
      exitAutoFire();
      return;
    }

    // generic pitch to fire balls
    const data = MachineContextHelper.getTroubleshootingMS(machine, {
      id: TroubleshootingPitchID.EmptyCarousel as string,
      video_uuid: StaticVideoType.empty_carousel,
      wheels: {
        w1: 2000,
        w2: 1600,
        w3: 1600,
      },
      rapid: true,
    });

    if (!data.msg) {
      return;
    }

    const sendResult = await sendTarget({
      source: COMPONENT_NAME,
      pitch: data.pitch,
      ref_py: data.pitch._mx_ms.py,
      msMsg: data.msg,
      force: true,
    });

    if (!sendResult.success) {
      NotifyHelper.error({
        message_md: t('settings.empty-carousel-failed-to-send-msg'),
      });
      return;
    }

    NotifyHelper.warning({
      message_md: t('settings.empty-carousel-warning-msg', {
        x: AUTOFIRE_DELAY_SEC,
      }),
    });

    setIgnoreR2fUntil(addSeconds(new Date(), AUTOFIRE_DELAY_SEC));

    setPitch(data.pitch);

    if (lastBallCount !== 1) {
      dropball({
        source: `${COMPONENT_NAME}: starting empty-carousel`,
        notifyResult: false,
      });
    }

    setSpecialMode('empty-carousel');
    setAutoFire(true);
  }, [
    autoFire,
    isActive,
    machine,
    dropball,
    exitAutoFire,
    sendTarget,
    setAutoFire,
    setSpecialMode,
  ]);

  // AF safety precautions
  useEffect(() => {
    // prevents leftover auto-fire from elsewhere causing EC to loop immediately
    setAutoFire(false);

    return () => {
      // exit AF for safety
      exitAutoFire();
    };
  }, []);

  // handle when r2f changes
  useEffect(() => {
    if (!autoFire) {
      return;
    }

    // e.g. user loses control of the machine while emptying carousel
    if (!isActive) {
      exitAutoFire();
      return;
    }

    if (isFuture(ignoreR2fUntil)) {
      return;
    }

    if (!r2fReady) {
      // not ready to fire
      return;
    }

    setIgnoreR2fUntil(addSeconds(new Date(), AUTOFIRE_WAIT_SEC));

    fire({
      pitch: pitch,
      fireData: {
        tags: 'BALLDUMP',
        mode: 'auto',
        training: false,
        trigger: COMPONENT_NAME,
        training_mode: undefined,
        hitter_id: undefined,
      },
    });
  }, [
    r2fDate, // * listen to this so that ignoreR2fUntil gets regularly updated
    r2fReady,
    autoFire,
    isActive,
    ignoreR2fUntil,
    lastMSHash,
    pitch,
    exitAutoFire,
  ]);

  const handleBallStatus = useCallback(
    (event: CustomEvent) => {
      // not auto-firing, nothing to disable
      if (!autoFire) {
        return;
      }

      const data: IBallStatusMsg = event.detail;

      if (data.ball_count > 0) {
        // reset count as soon as any balls are detected
        setDropRetries(0);
      }

      if (data.ball_count === 1) {
        return;
      }

      if (data.ball_count > 1) {
        exitAutoFire();

        NotifyHelper.warning({
          message_md: t('settings.empty-carousel-multiple-balls-msg'),
          delay_ms: 0,
        });
        return;
      }

      // no ball in the machine
      if (dropRetries < AUTOFIRE_DROP_RETRIES) {
        setDropRetries((prev) => prev + 1);
        return;
      }

      // exhausted retries, stop emptying carousel for safety
      NotifyHelper.warning({
        message_md: t('settings.empty-carousel-exit-msg', {
          x: AUTOFIRE_DROP_RETRIES,
        }),
      });

      exitAutoFire();
    },
    [autoFire, dropRetries, exitAutoFire]
  );

  // listen for ball status
  useEffect(() => {
    console.debug(`listening to ball status as of ${Date.now()}`);

    WebSocketHelper.on(WsMsgType.M2U_BallStatus, handleBallStatus);
    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_BallStatus, handleBallStatus);
    };
  }, [handleBallStatus]);

  // automatically stop auto-firing if control of machine is lost
  useEffect(() => {
    if (!autoFire) {
      return;
    }

    if (isActive) {
      return;
    }

    // e.g. user loses control of the machine while emptying carousel
    exitAutoFire();
  }, [autoFire, isActive]);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <SettingForm>
        {!isActive && <InactiveCallout />}

        <SettingRow
          header="main.inspect"
          description={<Text>{t('settings.inspect-msg')}</Text>}
          input={
            <SettingButton
              label="main.inspect"
              disabled={!isActive}
              onClick={() => openDialog(MachineDialogMode.Inspect)}
            />
          }
        />
        <SettingRow
          separatorBefore
          header="common.empty-carousel"
          description={<Text>{t('settings.empty-carousel-msg')}</Text>}
          input={
            <SettingButton
              label={autoFire ? 'common.stop' : 'common.start'}
              disabled={!isActive}
              color={autoFire ? RADIX.COLOR.WARNING : undefined}
              onClick={() => toggleAutoFire()}
            />
          }
        />
        <SettingRow
          separatorBefore
          header="common.lower-machine"
          description={<Text>{t('settings.reset-position-msg')}</Text>}
          input={
            <SettingButton
              label="common.lower-machine"
              disabled={!isActive}
              onClick={() => specialMsTarget(SpecialMsPosition.home)}
            />
          }
        />
        <SettingRow
          separatorBefore
          header="common.park-machine"
          description={<Text>{t('settings.park-machine-msg')}</Text>}
          input={
            <SettingButton
              label="common.park-machine"
              disabled={!isActive}
              onClick={() => specialMsTarget(SpecialMsPosition.park)}
            />
          }
        />
        <SettingRow
          separatorBefore
          header="common.hardware-diagnostic"
          description={<Text>{t('settings.hardware-diagnostic-msg')}</Text>}
          input={
            <SettingButton
              label="common.hardware-diagnostic"
              disabled={!isActive}
              onClick={async () => {
                const lastRT = lastDcComplete.find(
                  (m) => m.type === DataCollectionType.RepeatabilityTest
                );

                if (!lastRT || !lastRT.complete) {
                  NotifyHelper.warning({
                    message_md:
                      'Please complete one repeatability test for this machine before trying again.',
                  });
                  return;
                }

                const openCallback = (hwd: IDataCollection) => {
                  // close this dialog
                  setDialogSettings(undefined);

                  // open the hw diagnostic dialog
                  openDialog(MachineDialogMode.HwDiagnostic, {
                    hwd: hwd,
                  });
                };

                // upsert data collection record based on the ref RT details
                const existing = first(
                  await DataCollectionsService.findLastInProgress({
                    machineID: machine.machineID,
                    ball_type: machine.ball_type,
                    type: DataCollectionType.HardwareDiagnostic,
                    // uses the same ref list
                    listID: lastRT.listID,
                    // associated with the same RT instance
                    refID: lastRT._id,
                  })
                );

                if (existing) {
                  // use the existing incomplete collection to resume
                  console.debug(
                    `Resuming with existing HWD ${existing._id}`,
                    existing
                  );
                  openCallback(existing);
                  return;
                }

                const pitches: ITrainingPitch[] = [];

                const HWD_PITCH_TYPES = [
                  PitchType.FourSeamFastball,
                  PitchType.Slider,
                  PitchType.Curveball,
                ];

                HWD_PITCH_TYPES.forEach((type) => {
                  // find the first RHP (i.e. px <= 0) pitch of each specified type
                  const found = lastRT.pitches?.find(
                    (p) => !p.skipped && p.bs.px <= 0 && p.type === type
                  );

                  if (found) {
                    pitches.push(found);
                  }
                });

                if (pitches.length === 0) {
                  NotifyHelper.error({
                    message_md: `Failed to find any unskipped ${HWD_PITCH_TYPES.join(
                      ' / '
                    )} pitches to be used for hardware diagnostics.`,
                    buttons: [
                      {
                        label: 'Copy Repeatability ID',
                        dismissAfterClick: true,
                        onClick: () => {
                          navigator.clipboard.writeText(lastRT._id);

                          NotifyHelper.success({
                            message_md: t('common.x-copied-to-clipboard', {
                              x: 'ID',
                            }),
                          });
                        },
                      },
                    ],
                  });
                  return;
                }

                // create a new one based on the last RT data collection, don't use the helper fn to make sure refID and refCollectionID are set correctly
                const hwd = await DataCollectionsService.createOne({
                  user: current.email,
                  machineID: machine.machineID,
                  ball_type: machine.ball_type,
                  type: DataCollectionType.HardwareDiagnostic,

                  // always create a new collectionID
                  collectionID: StringHelper.getCollectionID(),
                  // always use the listID from the last RT so pitches line up
                  listID: lastRT.listID,

                  // * IMPORTANT SETTINGS
                  refID: lastRT._id, // for associating this HWD w/ a specific RT
                  refCollectionID: lastRT.collectionID, // for finding the shots to compare with later
                  include_low_confidence: lastRT.include_low_confidence, // to ensure the same aiming shots can be found
                  lock_low_confidence: lastRT.include_low_confidence
                    ? true
                    : false, // disallow users from rejecting LC if RT included LC
                  pitches: pitches, // to ensure the same pitches are used (regardless of model updates)
                });

                console.debug(`Created new HWD ${hwd._id}`, hwd);
                openCallback(hwd);
              }}
            />
          }
        />
        <SettingRow
          separatorBefore
          header="common.restart"
          description={<Text>{t('settings.restart-msg')}</Text>}
          input={
            <SettingButton
              label="common.restart"
              color={RADIX.COLOR.WARNING}
              disabled={!isActive}
              onClick={() => {
                setDialogRestart(Date.now());
              }}
            />
          }
        />
      </SettingForm>

      {dialogRestart && (
        <CommonConfirmationDialog
          key={dialogRestart}
          identifier="MaintenanceConfirmRestart"
          title="common.restart"
          content={<Box>{t('common.machine-not-in-use-msg')}</Box>}
          action={{
            onClick: () => restartOS('SidebarConnectionButton'),
          }}
        />
      )}
    </ErrorBoundary>
  );
};
